import { useCallback, useEffect, useRef, useState } from 'react'
import type { SessionJourney } from '@shared/sessions/types'
import { useAuthOptional } from '../../../contexts/auth-context'
import {
  isActiveSessionTravelStartPending,
  publishActiveSessionPhase,
  useActiveSessionTravelStartPending,
  useActiveSession,
} from '../../../stores/active-session-store'
import {
  publishLiveJourney,
  publishLiveJourneyPresence,
  resetLiveJourney,
  useLiveJourneyPublication,
} from '../../../stores/live-journey-store'
import { recordLivePingEvent } from '../../../services/live-ping-telemetry'
import { getSessionsRepository } from '../data/sessions-repository-provider'
import { useEnRouteGpsTracking, type JourneyPingPayload } from './use-en-route-gps-tracking'
import { useNativeLivePing } from './use-native-live-ping'

const ESTIMATE_WARNING = "Couldn't get precise location — using estimate"

/** The server refused the ping because the journey is over / not ours. */
function isJourneyGone(err: unknown): boolean {
  const e = err as { status?: number; code?: string } | null
  if (!e || typeof e !== 'object') return false
  if (e.status === 403 || e.status === 404 || e.status === 409) return true
  // 400 NOT_FACE_TO_FACE: a phone / video session has no journey at all.
  return e.status === 400 && (e.code === 'JOURNEY_NOT_ACTIVE' || e.code === 'NOT_FACE_TO_FACE')
}

/**
 * APP-WIDE owner of the provider's LIVE en-route journey pings.
 *
 * WHY (2026-10-19 field test, build 126): the ping cadence (`useEnRouteGpsTracking`)
 * and the native hand-over (`useNativeLivePing`) were mounted inside
 * `JourneyFlowCard`, i.e. only while the session-detail screen was open. Native
 * posted once, then nothing for 9 minutes; web pings stopped the moment the
 * provider left the session screen — even with the app in the foreground.
 *
 * Now both live HERE, mounted once in the authenticated shell (next to the
 * active-session banner) and driven by the active-session store:
 *
 *  - ARMED whenever the signed-in provider's active session is `travelling`
 *    (journey `en_route`), whichever screen is open;
 *  - the web cadence runs while the app is foregrounded on ANY screen; native
 *    posts while it is backgrounded (hand-over switch derived natively);
 *  - DISARMED when the journey ends/arrives (the ping response says so, or the
 *    store leaves `travelling`), the session changes, the user signs out or
 *    switches account (the controller is keyed by user id, and
 *    `resetActiveSessionForSignOut()` still disarms first).
 *
 * `JourneyFlowCard` keeps UI only: it reads position/GPS state from
 * `live-journey-store` and adopts the journey this hook publishes.
 *
 * Journey source of truth: seeded by the session detail (published into the
 * live-journey store whenever its journey changes) or, when no detail screen
 * is mounted (cold start, reload mid-journey), fetched once on activation and
 * again whenever the app becomes visible. Ping / backfill responses update it.
 */
export function useLiveJourneyPings(): void {
  const snapshot = useActiveSession()
  const auth = useAuthOptional()
  const signedIn = Boolean(auth && !auth.isLoading && auth.user)

  const sessionUuid = snapshot?.sessionUuid ?? null
  const memberName = snapshot?.memberName ?? ''
  // The server says this journey is over / not ours (cancelled or ended on
  // another device, 403/404/409). Keyed on the phase start so the NEXT
  // active-session refresh (a new travel phase) re-enables pinging, but the
  // shell store's per-sample snapshot updates do not.
  const phaseKey = snapshot ? `${snapshot.sessionUuid}:${snapshot.phaseStartedAt}` : null
  const [inactiveKey, setInactiveKey] = useState<string | null>(null)
  const inactive = inactiveKey != null && inactiveKey === phaseKey
  const phaseKeyRef = useRef<string | null>(phaseKey)
  phaseKeyRef.current = phaseKey
  const travelling = signedIn && snapshot?.phase === 'travelling' && sessionUuid != null && !inactive

  // The tap's journey start is still unanswered: the first journey load is
  // skipped meanwhile and retried HERE when the flag clears — independent of
  // whether the journey card is still mounted.
  const travelStartPending = useActiveSessionTravelStartPending(sessionUuid)

  const [journeyState, setJourneyState] = useState<{ sessionUuid: string; journey: SessionJourney } | null>(null)
  const journey = journeyState && journeyState.sessionUuid === sessionUuid ? journeyState.journey : null
  const [warning, setWarning] = useState<string | null>(null)

  const journeyRef = useRef<SessionJourney | null>(journey)
  const sessionRef = useRef<string | null>(sessionUuid)
  journeyRef.current = journey
  sessionRef.current = sessionUuid

  /** Adopt + publish a journey the controller learned from a ping / backfill / fetch. */
  const adopt = useCallback((forSession: string, next: SessionJourney) => {
    if (sessionRef.current !== forSession) return
    journeyRef.current = next
    setJourneyState({ sessionUuid: forSession, journey: next })
    publishLiveJourney({ sessionUuid: forSession, journey: next, origin: 'controller' })
  }, [])

  // The session detail publishes its own journey changes (start journey,
  // its poll) — adopt them so the controller never pings from a stale one.
  const published = useLiveJourneyPublication(sessionUuid)
  useEffect(() => {
    if (!travelling || sessionUuid == null) return
    if (published?.origin !== 'detail' || !published.journey) return
    if (published.journey === journeyRef.current) return
    journeyRef.current = published.journey
    setJourneyState({ sessionUuid, journey: published.journey })
  }, [published, travelling, sessionUuid])

  // No journey yet (cold start / reload mid-journey / no detail screen):
  // fetch it, and refresh whenever the app becomes visible again.
  useEffect(() => {
    if (!travelling || sessionUuid == null) return
    let cancelled = false
    const load = () => {
      // The Navigate tap started native tracking before the server confirmed
      // the journey: a fetch now would only read "no journey yet".
      if (isActiveSessionTravelStartPending(sessionUuid)) return
      const repo = getSessionsRepository()
      if (!repo.getSessionJourney) return
      const sentWith = journeyRef.current
      void repo
        .getSessionJourney(sessionUuid)
        .then((data) => {
          if (cancelled || !data.journey) return
          // The journey changed while this request was in flight (the start
          // response / a ping / the detail screen published a newer one): the
          // late read predates it and must not overwrite it — a stale "none"
          // would stop the FGS and disarm native.
          if (journeyRef.current !== sentWith) return
          adopt(sessionUuid, data.journey)
        })
        .catch(() => {
          /* offline — the next resume / ping response refreshes it */
        })
    }
    if (journeyRef.current == null) load()
    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [travelling, sessionUuid, adopt, travelStartPending])

  const sendPing = useCallback(
    async (payload: JourneyPingPayload) => {
      const forSession = sessionRef.current
      if (!forSession) return
      const repo = getSessionsRepository()
      const current = journeyRef.current
      if (repo.pingJourney) {
        adopt(forSession, await repo.pingJourney(forSession, payload))
      } else if (current) {
        adopt(forSession, {
          ...current,
          pings: [
            ...current.pings,
            {
              lat: payload.lat,
              lng: payload.lng,
              timestamp: payload.timestamp,
              accuracy: payload.accuracy,
              stage: payload.stage,
              etaMinutes: payload.etaMinutes,
              locationSource: payload.locationSource,
              ...(payload.isMock ? { isMock: true } : {}),
            },
          ],
          stage: payload.stage,
        })
      }
    },
    [adopt],
  )

  /**
   * A ping was rejected because the journey is no longer active. Fetch the
   * real state and adopt it (`enRoute` → false → native disarms, cadence
   * stops). If even the GET says 403/404 the session is gone for us: mark it
   * inactive until the next active-session refresh.
   */
  const resolveGoneJourney = useCallback(
    async (forSession: string, forPhaseKey: string | null) => {
      const markInactive = () => {
        if (sessionRef.current === forSession) setInactiveKey(forPhaseKey)
      }
      const repo = getSessionsRepository()
      if (!repo.getSessionJourney) {
        markInactive()
        return
      }
      try {
        const data = await repo.getSessionJourney(forSession)
        if (!data.journey) {
          markInactive()
          return
        }
        adopt(forSession, data.journey)
      } catch (err) {
        const status = (err as { status?: number } | null)?.status
        if (status === 403 || status === 404) markInactive()
        // Offline / 5xx: the next cadence tick retries (one POST + one GET).
      }
    },
    [adopt],
  )

  const enRoute = journey?.status === 'en_route'

  // Native hand-over: armed whenever the journey is en route, on any screen.
  const nativeLivePing = useNativeLivePing({
    enabled: travelling && enRoute,
    sessionId: sessionUuid ?? '',
  })

  const { currentPosition, gpsActive, gpsUnavailable } = useEnRouteGpsTracking({
    enabled: travelling,
    journey,
    destination: snapshot?.geo ?? null,
    travelDurationMs: snapshot?.travelDurationMs ?? null,
    checkedIn: false,
    onPing: async (payload) => {
      const forSession = sessionRef.current
      const forPhaseKey = phaseKeyRef.current
      try {
        if (payload.locationSource === 'estimate') setWarning(ESTIMATE_WARNING)
        await sendPing(payload)
        if (payload.locationSource === 'gps') setWarning(null)
        nativeLivePing.refresh()
      } catch (err) {
        // The journey is over / not ours (cancelled elsewhere, 409 …): an
        // estimate retry would just fail the same way, twice per cadence,
        // app-wide. Re-read the journey instead — that disarms everything.
        if (forSession && isJourneyGone(err)) {
          await resolveGoneJourney(forSession, forPhaseKey)
          return
        }
        setWarning(ESTIMATE_WARNING)
        await sendPing({ ...payload, locationSource: 'estimate' })
      }
    },
    // Replayed background fixes → journey track only (route/km), never the
    // live position. Best-effort; the queue swallows failures.
    onBackfill: async (fixes, meta) => {
      const forSession = sessionRef.current
      const repo = getSessionsRepository()
      if (!forSession || !repo.backfillJourney) return
      const next = await repo.backfillJourney(forSession, fixes)
      // Intermediate 60-fix chunks may persist `arrived` on the server;
      // applying that locally would arm the dwell before later chunks
      // (drive-away / remaining dwell proof) have merged.
      if (meta && !meta.isLast) return
      adopt(forSession, next)
    },
  })

  // Live position / GPS state for the card's ETA tile.
  useEffect(() => {
    if (!travelling || sessionUuid == null) {
      publishLiveJourneyPresence(null)
      return
    }
    publishLiveJourneyPresence({ sessionUuid, currentPosition, gpsActive, gpsUnavailable, warning })
  }, [travelling, sessionUuid, currentPosition, gpsActive, gpsUnavailable, warning])

  // The ping response (or a poll) says the journey is no longer en route
  // while no session-detail screen is mounted: keep the shell store in step
  // so the banner / native service follow the arrival.
  useEffect(() => {
    if (!travelling || sessionUuid == null || !journey) return
    if (journey.status !== 'arrived') return
    publishActiveSessionPhase({
      sessionUuid,
      memberName,
      phase: 'arrived',
      phaseStartedAt: journey.arrivedAt ?? null,
    })
  }, [travelling, sessionUuid, memberName, journey])

  // Session change / end / sign-out: forget the previous session's journey.
  useEffect(() => {
    if (sessionUuid == null) {
      setJourneyState(null)
      setWarning(null)
      setInactiveKey(null)
      resetLiveJourney()
      return
    }
    setJourneyState((prev) => (prev && prev.sessionUuid !== sessionUuid ? null : prev))
    setWarning(null)
  }, [sessionUuid])

  // Unmount (sign-out / account switch re-keys the runner): the shared
  // live-journey store must not carry this user's journey or position into
  // the next one.
  useEffect(() => () => resetLiveJourney(), [])

  // Telemetry: why pinging is (not) happening, with no coordinates or tokens.
  const controllerState =
    travelling && enRoute ? 'armed' : travelling ? 'waiting_for_journey' : signedIn ? 'idle' : 'signed_out'
  const lastReportedRef = useRef<{ state: string; sessionUuid: string | null } | null>(null)
  useEffect(() => {
    const last = lastReportedRef.current
    // Nothing to say before the first journey (every app launch is `idle`).
    if (!last && (controllerState === 'idle' || controllerState === 'signed_out')) return
    if (last && last.state === controllerState && last.sessionUuid === sessionUuid) return
    lastReportedRef.current = { state: controllerState, sessionUuid }
    recordLivePingEvent('controller', { state: controllerState }, sessionUuid)
  }, [controllerState, sessionUuid])
}
